import { utils } from '@iyunbao/cr-engine';
import _get from 'lodash.get';

import { ValidateProps } from './type';

type IdeepAsset = (data1: any, data2: any) => boolean;
const UA = typeof window === 'undefined' ? '' : window.navigator.userAgent.toLowerCase();

// App内
export const isInApp = !!~UA.indexOf('iyunbao');
// 是否为window系统
export const isWindows = /windows|win32/.test(UA);
// 微信
export const isInWechat = !!~UA.indexOf('micromessenger');

/**
 * 对象检测
 * @param value
 */
export const isObject = (value: any) => typeof value === 'object' && value.constructor === Object;

/**
 * 转换字符串
 * @param {string | number} value 值
 * return {string}
 */
export const toString = (value: any) => {
  if (typeof value === 'number') {
    return value.toString();
  }

  return value;
};

export const passiveRefresh = (props) => {
  const { refreshOptionsPath, _crengine } = props;
  const { form } = _crengine || {};
  let _gupValue: any[] = [];
  let dtPath: string[] = [];

  if (refreshOptionsPath && form) {
    if (typeof refreshOptionsPath === 'string') {
      dtPath.push(refreshOptionsPath);
    } else if (Array.isArray(refreshOptionsPath)) {
      dtPath = [...refreshOptionsPath];
    }

    const values = form.getFieldsValue();

    dtPath.forEach((path, index) => {
      _gupValue[index] = _get(values, path);
    });
  }

  return (callback) => {
    if (_gupValue.length > 0) {
      const values = form.getFieldsValue();
      const nextValue: any[] = [];

      if (
        dtPath.every((path, index) => {
          const pValue = _get(values, path);

          nextValue[index] = pValue;

          return _gupValue[index] === pValue;
        })
      ) {
        return;
      }

      _gupValue = nextValue;
      callback(_gupValue);
    }
  };
};

/**
 * 深复制
 * @param {array|object} data 数据
 */
export const deepCopy = (data: any) => {
  if (Array.isArray(data)) {
    return data.slice().map((item) => deepCopy(item));
  }

  if (Object.prototype.toString.call(data) === '[object Object]') {
    const keys = Object.keys(data);
    const dataProto = Object.getPrototypeOf(data);
    const out = dataProto === Object.prototype ? {} : Object.create(dataProto);

    keys.reduce((cur, key) => {
      cur[key] = deepCopy(data[key]);
      return cur;
    }, out);

    return out;
  }

  return data;
};

/**
 * 深断言
 * @param {array|object} data1 数据1
 * @param {array|object} data2 数据2
 */
export const deepAsset: IdeepAsset = (data1: any, data2: any) => {
  return JSON.stringify(data1) === JSON.stringify(data2);
};

/**
 * 比对数组，过滤values 值
 * @param array
 * @param values
 */
export const difference = (array: string[], values?: string[]) => {
  if (!(array && Array.isArray(array) && array.length)) {
    return [];
  }

  if (!(values && Array.isArray(values) && values.length)) {
    return array;
  }

  const result: string[] = [];

  array.forEach((key) => {
    if (values.indexOf(key) < 0) {
      result.push(key);
    }
  });

  return result;
};

type DiffchangeProps = {
  [propName: string]: any;
};

/**
 * 比对触发数据
 * @param {string[]} attrs 属性表
 * @param {object}   from  数据1
 * @param {object}   to    数据2
 */
export const diffchange = (attrs: string[], from: DiffchangeProps, to: DiffchangeProps) => {
  return attrs.some((key) => from[key] !== to[key]);
};

/**
 * 获取form唯一标志
 * @param {string} name 名字
 * @param {string} path 路径
 */
export const getFormId = (name?: string, path?: string | string[]) => {
  let getPath: string[] = [];

  if (Array.isArray(path)) {
    if (name && typeof name === 'string') {
      getPath = path.concat(name);
    } else {
      getPath = path;
    }
  } else if (typeof path === 'string') {
    if (typeof name === 'string') {
      getPath = path.split('.').concat(name);
    } else {
      getPath = path.split('.');
    }
  } else if (typeof name === 'string') {
    getPath = getPath.concat(name);
  }

  return getPath.join('.');
};

/**
 * 去前后空格
 * @param {string} value  值
 * return {any}
 */
export function toTrim(value: any) {
  // isWindows
  // 环境 系统window10／chrome浏览器／搜狗输入框／连打，按下失效
  if (typeof value !== 'string' || isWindows) {
    return value;
  }

  if (typeof value === 'string') {
    if (!String.prototype.trim) {
      return value.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '');
    }

    return value.trim();
  }

  return value;
}

/**
 * 生成数据地址
 * @param {object} obj 组件数据
 * return {string}
 */
export function makeBindPath(obj) {
  const { path, dataBind } = obj;

  return path ? `${path}.${dataBind}` : dataBind;
}

/**
 * 解决返回报文不同，解析API结果
 * @param {object}  res   返回报文
 * return { code: number, result?: object, message: string }
 */
export function parseAPIResult(res: any): any {
  if (!res) {
    return {
      code: 2001,
      message: '请求异常',
    };
  }

  let newResult: any = {};

  if (res.result && res.result === 'success') {
    newResult = {
      code: 0,
      result: res.content,
      message: res.reason || '',
    };
  } else if (res.result && res.result === 'fail') {
    newResult = {
      code: 2002,
      message: res.reason,
    };
    // { isSuccess: false, errorCode: '20102', errorMsg: '手机号格式不正确', result: null }
  } else if (typeof res.isSuccess !== 'undefined') {
    newResult = {
      code: res.isSuccess ? 0 : res.errorMsg || 2002,
      result: res.result,
      message: res.errorMsg,
    };
    // 新接口报文
  } else {
    newResult = res;
  }

  // 未加工结果
  newResult.sourceResult = res;

  return newResult;
}

export interface TypeToPathProps {
  dataBind?: string;
  paramType?: 'string' | 'number' | 'boolean' | 'array' | 'object';
  [propName: string]: any;
}

/**
 * 提取数据，类型
 * @param   props     组件属性
 * @config  dataBind  绑定返回数据
 * @config  paramType 返回数据类型（提供回调、后端），可选值为string 、number、boolean、array、object
 */
export const typeToPath = (props: TypeToPathProps) => {
  const { dataBind, paramType } = props;

  if (dataBind && paramType && paramType === 'array') {
    return [`${dataBind}[0]`];
  }

  return [];
};

/**
 * 替换自定义验证
 * @param validate {object} 验证规则
 * return {object}
 */
export const replaceValidate = (validate: ValidateProps) => {
  if (!(validate && validate.rules)) {
    return validate;
  }

  const { rules } = validate;

  if (rules && Array.isArray(rules) && rules.length) {
    rules.map((item: any) => {
      if (item.validate && typeof item.validate === 'string' && utils.validateRule[item.validate]) {
        item.validator = utils.validateRule[item.validate];
        delete item.message;
      }

      return item;
    });
  }

  return validate;
};

export function getBase64(file: File): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

/**
 * 根据原始对象获取新的对象数据
 * @param obj 原始对象
 * @param keys key值数组
 */
export function clearObj(obj: any) {
  if (isObject(obj)) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] === undefined) {
        delete obj[key];
      }
    });
  }

  return obj;
}

/**
 * 原始对象移除函数key
 * @param {object} 原始对象
 * return {object}
 */
export function clearFunc(obj: any) {
  if (isObject(obj)) {
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === 'function') {
        delete obj[key];
      }
    });
  }

  return obj;
}

/**
 * 合并对象
 * @param {object} data1  主数据
 * @param {object} data2  数据2
 * return {object}
 */
export function mergeObj(data1: any, data2: any) {
  if (isObject(data1) && isObject(data2)) {
    Object.keys(data2).forEach((key) => {
      if (data2[key] !== null && data2[key] !== undefined) {
        data1[key] = data2[key];
      }
    });
  }

  return data1;
}

/**
 * 获取样式浏览器是否支持
 * return string
 */
export function getSafeSupport() {
  let name = '';

  if (!('CSS' in window && typeof CSS.supports === 'function')) {
    return '';
  }

  if (CSS.supports('padding-bottom: env(safe-area-inset-bottom)')) {
    name = 'env';
  } else if (CSS.supports('padding-bottom: constant(safe-area-inset-bottom)')) {
    name = 'constant';
  }

  return name;
}

/**
 * 获取刘海屏距离
 * return string
 */
export function getSafeIphoneX() {
  const support = getSafeSupport();

  if (!support) {
    return 0;
  }

  const element = document.createElement('div');

  element.style.paddingBottom = `${support}(safe-area-inset-bottom)`;
  document.body.appendChild(element);

  const padding = parseInt(window.getComputedStyle(element).paddingBottom, 10);

  document.body.removeChild(element);

  return padding;
}

/**
 * 函数节流
 * @param {function} func  函数
 * @param {number}   limit 节流时间，默认时间500
 */
export function throttle(func: any, limit: number = 500) {
  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }

  let last = 0;

  return (...args) => {
    const now = Date.now();
    if (now - last >= limit) {
      func && func(...args);
      last = now;
    }
  };
}

/**
 * 去除字符串中所有空格
 * @param str 需要去除空格的字符串
 * @returns 去除空格后的字符串
 */
export function removeBlankSpace(str: string): string {
  if (str?.replace) {
    return str.replace(/\s/g, '');
  }
  return str;
}
